import { features, comparisonData } from '@/mocks/siteData';

export function WhatIsSection() {
  return (
    <section className="py-14 md:py-20 bg-background-50" id="about">
      <div className="w-full px-4 md:px-6">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-10">
            <h2 className="text-2xl md:text-3xl font-heading font-semibold text-foreground-900 mb-4">
              What Is Basetao Spreadsheet?
            </h2>
            <p className="text-sm md:text-base text-foreground-600 max-w-3xl mx-auto leading-relaxed">
              A <strong>Basetao Spreadsheet</strong> is a curated product discovery tool used by thousands of international shoppers to find, compare and purchase items from Chinese e-commerce platforms through the Basetao shopping agent. Instead of manually searching through endless listings on Taobao, Weidian and 1688, users access organized spreadsheets that contain verified product links, seller ratings, QC photos and accurate pricing — making the entire shopping experience faster, safer and more efficient.
            </p>
          </div>

          <div className="overflow-x-auto rounded-xl border border-background-200/70">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-background-100">
                  <th className="text-left px-6 py-4 font-semibold text-foreground-800">Feature</th>
                  <th className="text-left px-6 py-4 font-semibold text-foreground-800">Without Spreadsheet</th>
                  <th className="text-left px-6 py-4 font-semibold text-foreground-800">With Basetao Spreadsheet</th>
                </tr>
              </thead>
              <tbody>
                {comparisonData.map((row, idx) => (
                  <tr key={row.criteria} className={idx % 2 === 0 ? 'bg-background-50' : 'bg-background-100/50'}>
                    <td className="px-6 py-3.5 font-medium text-foreground-800">{row.criteria}</td>
                    <td className="px-6 py-3.5 text-foreground-500">{row.without}</td>
                    <td className="px-6 py-3.5 text-primary-700 font-medium">{row.with}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </section>
  );
}

export function WhySection() {
  return (
    <section className="py-14 md:py-20 bg-background-100">
      <div className="w-full px-4 md:px-6">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-10">
            <h2 className="text-2xl md:text-3xl font-heading font-semibold text-foreground-900 mb-3">
              Why Users Search For Basetao Spreadsheet
            </h2>
            <p className="text-sm md:text-base text-foreground-600 max-w-2xl mx-auto">
              Thousands of shoppers worldwide rely on Basetao spreadsheets every day. Here is what makes them indispensable.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
            {features.map((feat) => (
              <div
                key={feat.title}
                className="group p-6 md:p-7 rounded-xl bg-background-50 border border-background-200/70 hover:border-primary-200 transition-all duration-200"
              >
                <div className="w-11 h-11 flex items-center justify-center rounded-lg bg-primary-100 text-primary-500 mb-4 group-hover:bg-primary-500 group-hover:text-white transition-colors duration-200">
                  <i className={`${feat.icon} text-xl`}></i>
                </div>
                <h3 className="text-base md:text-lg font-heading font-semibold text-foreground-900 mb-2">
                  {feat.title}
                </h3>
                <p className="text-sm text-foreground-600 leading-relaxed">
                  {feat.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

export function ComparisonSection() {
  return (
    <section className="py-14 md:py-20 bg-background-50">
      <div className="w-full px-4 md:px-6">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-10">
            <h2 className="text-2xl md:text-3xl font-heading font-semibold text-foreground-900 mb-3">
              Basetao Spreadsheet vs Traditional Searching
            </h2>
            <p className="text-sm md:text-base text-foreground-600 max-w-2xl mx-auto">
              See how using a Basetao spreadsheet transforms your shopping workflow compared to searching manually.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
            <div className="p-6 md:p-8 rounded-xl bg-background-100 border border-background-200/70">
              <div className="flex items-center gap-3 mb-5">
                <div className="w-9 h-9 flex items-center justify-center rounded-lg bg-foreground-200 text-foreground-600">
                  <i className="ri-search-line text-lg"></i>
                </div>
                <h3 className="text-lg font-heading font-semibold text-foreground-800">Traditional Manual Search</h3>
              </div>
              <ul className="space-y-3">
                {[
                  'Hours spent browsing endless listings',
                  'No seller quality verification',
                  'Hidden costs and surprise fees',
                  'High risk of receiving wrong items',
                  'Overwhelming for beginners',
                ].map((item, i) => (
                  <li key={i} className="flex items-start gap-2.5 text-sm text-foreground-600">
                    <i className="ri-close-circle-line text-red-400 mt-0.5 shrink-0"></i>
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="p-6 md:p-8 rounded-xl bg-primary-50/60 border border-primary-200/60">
              <div className="flex items-center gap-3 mb-5">
                <div className="w-9 h-9 flex items-center justify-center rounded-lg bg-primary-500 text-white">
                  <i className="ri-file-list-3-line text-lg"></i>
                </div>
                <h3 className="text-lg font-heading font-semibold text-foreground-800">With Basetao Spreadsheet</h3>
              </div>
              <ul className="space-y-3">
                {[
                  'Find products in minutes with organized links',
                  'Pre-vetted sellers with verified ratings',
                  'Clear shipping cost estimates upfront',
                  'QC photos integrated into workflow',
                  'Step-by-step guidance for new users',
                ].map((item, i) => (
                  <li key={i} className="flex items-start gap-2.5 text-sm text-foreground-700">
                    <i className="ri-checkbox-circle-line text-primary-500 mt-0.5 shrink-0"></i>
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}